import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, IndianRupee, Download, Filter, RefreshCw, Loader2, AlertTriangle, Search, Wand2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

/**
 * HR → Folding Category Wages
 *
 * Aggregates folding_transactions across staff × category2 (resolved
 * per-bundle from item_master), applies per-category2 piece rates
 * (with the flat INH_FOLDING fallback), and exports to CSV.
 */
const FoldingCategoryWages = () => {
  const navigate = useNavigate();
  const today = new Date().toISOString().slice(0, 10);
  const lastMonth = new Date(); lastMonth.setDate(lastMonth.getDate() - 30);
  const [dateFrom, setDateFrom] = useState(lastMonth.toISOString().slice(0, 10));
  const [dateTo, setDateTo] = useState(today);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ rows: 0, total_usns: 0, total_amount: 0 });
  const [loading, setLoading] = useState(false);

  // Drill-down state
  const [drillOpen, setDrillOpen] = useState(false);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillRows, setDrillRows] = useState([]);
  const [drillTotals, setDrillTotals] = useState({});
  const [drillSearch, setDrillSearch] = useState('');

  // Bundle-source trace state (admin diagnostic for blank sort numbers)
  const [traceOpen, setTraceOpen] = useState(false);
  const [traceLoading, setTraceLoading] = useState(false);
  const [traceData, setTraceData] = useState(null);
  const [traceExpanded, setTraceExpanded] = useState({});

  // Heal category2 state (admin only)
  const [healOpen, setHealOpen] = useState(false);
  const [healLoading, setHealLoading] = useState(false);
  const [healPreview, setHealPreview] = useState(null);
  const [healResult, setHealResult] = useState(null);
  const isAdmin = (typeof window !== 'undefined' && ['Admin', 'Super Admin'].includes(localStorage.getItem('role') || ''));

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const { data } = await apiClient.get(`/hr/folding-category-wages?${params}`);
      setRows(data.rows || []);
      setTotals(data.totals || { rows: 0, total_usns: 0, total_amount: 0 });
      // 2026-05-26 — Drill data is returned alongside the main report so
      // the "View Sort Numbers" modal can open instantly without a second
      // (frequently-timing-out) round-trip to /uncategorised.
      setDrillRows(data.uncat_rows || []);
      setDrillTotals({
        uncategorised_sku_count: data.totals?.uncategorised_sku_count ?? 0,
        uncategorised_usns: data.totals?.uncategorised_usns ?? 0,
        uncategorised_folds: data.totals?.uncategorised_folds ?? 0,
        uncategorised_in_master: data.totals?.uncategorised_in_master ?? 0,
        uncategorised_missing_in_master: data.totals?.uncategorised_missing_in_master ?? 0,
        // 2026-05-26 (+8) — Root-cause histogram surfaced inline on the
        // uncat banner so operators can definitively see WHERE the
        // uncategorised USNs come from (vs. inferring from the trace).
        uncategorised_breakdown: data.totals?.uncategorised_breakdown ?? null,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const handleExportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const resp = await apiClient.get(`/hr/folding-category-wages/export.csv?${params}`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `folding_category_wages_${dateFrom || 'all'}_to_${dateTo || 'all'}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'CSV export failed');
    }
  };

  const openDrillDown = () => {
    // 2026-05-26 — Drill data is now pre-loaded with the main report so we
    // no longer need a separate (slow, proxy-timeout-prone) fetch here.
    setDrillSearch('');
    setDrillOpen(true);
  };

  const handleDrillExportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const resp = await apiClient.get(`/hr/folding-category-wages/uncategorised/export.csv?${params}`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `folding_uncategorised_skus_${dateFrom || 'all'}_to_${dateTo || 'all'}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'CSV export failed');
    }
  };

  const filteredDrillRows = useMemo(() => {
    const q = drillSearch.trim().toLowerCase();
    if (!q) return drillRows;
    return drillRows.filter((r) =>
      [r.sku_code, r.design_code, r.design_name, r.color, r.size, r.category4, r.brand]
        .some((v) => String(v || '').toLowerCase().includes(q))
    );
  }, [drillRows, drillSearch]);

  const openBundleTrace = async () => {
    setTraceOpen(true);
    setTraceLoading(true);
    setTraceExpanded({});
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      params.set('sample_size', '20');
      const { data } = await apiClient.get(`/hr/folding-uncategorised/bundle-trace?${params}`);
      setTraceData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load bundle trace');
      setTraceData(null);
    } finally {
      setTraceLoading(false);
    }
  };

  // 2026-05-26 — Per-bucket CSV export for the legacy-chain trace.
  // User wants the full list (not just the in-modal sample) so the
  // master-data team can act on each finding — especially the
  // `unresolved_no_greige_fg` bucket whose rows tell them exactly
  // which sort numbers need to be added to greige_sort_nos.
  const [bucketDownloading, setBucketDownloading] = useState(null);
  const handleBucketCSV = async (bucket) => {
    setBucketDownloading(bucket);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      params.set('bucket', bucket);
      const resp = await apiClient.get(
        `/hr/folding-uncategorised/bundle-trace/export-csv?${params}`,
        { responseType: 'blob' },
      );
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `folding-blank-sort-${bucket}-${dateFrom || 'all'}-to-${dateTo || 'all'}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${bucket} CSV`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bucket CSV export failed');
    } finally {
      setBucketDownloading(null);
    }
  };

  const openHealDialog = async () => {
    setHealOpen(true);
    setHealLoading(true);
    setHealResult(null);
    setHealPreview(null);
    try {
      const { data } = await apiClient.post(
        '/hr/folding-category-wages/heal-category2-from-category4?dry_run=true',
      );
      setHealPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load heal preview');
    } finally {
      setHealLoading(false);
    }
  };

  const runHeal = async () => {
    setHealLoading(true);
    try {
      const { data } = await apiClient.post(
        '/hr/folding-category-wages/heal-category2-from-category4',
      );
      setHealResult(data);
      toast.success(data.message || `Healed ${data.updated_count} rows`);
      // Refresh report to reflect newly categorised wages
      await fetchReport();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Heal failed');
    } finally {
      setHealLoading(false);
    }
  };

  // Group rows by staff for prettier rendering — keeps category2 breakdown
  // inline so finance can verify the rate applied.
  const groupedByStaff = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const k = r.staff_id || '(unknown)';
      if (!map.has(k)) {
        map.set(k, { staff_id: r.staff_id, staff_name: r.staff_name, categories: [], total_usns: 0, total_amount: 0 });
      }
      const g = map.get(k);
      g.categories.push(r);
      g.total_usns += r.usn_count;
      g.total_amount += r.amount;
    }
    return Array.from(map.values()).sort((a, b) => b.total_amount - a.total_amount);
  }, [rows]);

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="folding-cat-wages-page">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/sections/accounts/hr')}
                    className="text-[#F9FAFB] hover:bg-white/10" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/30 flex items-center justify-center">
                <IndianRupee className="w-5 h-5" />
              </div>
              <div>
                <h1 className="text-xl font-bold font-display">Folding Wages — by Category 2</h1>
                <p className="text-sm text-[#E8DCC8]">Folding staff payout grouped by item-master Category 2</p>
              </div>
            </div>
          </div>
          <Button variant="outline" onClick={handleExportCSV} disabled={loading || rows.length === 0}
                  className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                  data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <Card className="mb-6">
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-lg flex items-center gap-2"><Filter className="w-5 h-5" /> Filters</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-12 gap-3">
            <div className="md:col-span-3">
              <Label htmlFor="date-from" className="text-xs">Date From</Label>
              <Input id="date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                     data-testid="date-from-input" />
            </div>
            <div className="md:col-span-3">
              <Label htmlFor="date-to" className="text-xs">Date To</Label>
              <Input id="date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                     data-testid="date-to-input" />
            </div>
            <div className="md:col-span-2 flex items-end">
              <Button onClick={fetchReport} disabled={loading} className="w-full" data-testid="apply-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Apply
              </Button>
            </div>
            <div className="md:col-span-4 flex items-end justify-end gap-3 text-sm">
              <Badge variant="outline" data-testid="total-staff">Staff: {groupedByStaff.length}</Badge>
              <Badge variant="outline" data-testid="total-usns">USNs: {totals.total_usns.toLocaleString()}</Badge>
              <Badge className="bg-green-100 text-green-900" data-testid="total-amount">
                ₹{totals.total_amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {totals.uncategorised_usns > 0 ? (
          <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3"
               data-testid="uncategorised-banner">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5" />
                <div>
                  <div className="font-semibold text-amber-900">
                    {totals.uncategorised_usns.toLocaleString()} USNs across {totals.uncategorised_folds.toLocaleString()} folds are uncategorised
                  </div>
                  <div className="text-sm text-amber-800">
                    Their sort numbers are missing <code className="px-1 bg-white/60 rounded">category2</code> in Item Master.
                    Click below to view the list and patch Item Master so payroll attribution becomes accurate.
                  </div>
                </div>
              </div>
              <Button size="sm" variant="outline"
                      className="border-amber-400 bg-white hover:bg-amber-100 text-amber-900"
                      onClick={openDrillDown} data-testid="view-uncat-btn">
                View Sort Numbers
              </Button>
            </div>
            {drillTotals?.uncategorised_breakdown ? (
              <div className="mt-3 pt-3 border-t border-amber-200" data-testid="uncat-root-cause-breakdown">
                <div className="text-xs font-semibold text-amber-900 uppercase tracking-wide mb-2">
                  Root-cause breakdown — where these USNs actually come from
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                  {[
                    { k: 'non_blank_barcode_no_item_master_cat2',
                      label: 'Non-blank barcode missing in Item Master',
                      hint: 'Patch item_master.category2 for the listed SKUs' },
                    { k: 'blank_barcode_no_ibr',
                      label: 'Blank barcode + no IBR row',
                      hint: 'Orphan bundle — needs IBR backfill' },
                    { k: 'blank_barcode_ibr_no_sort_no',
                      label: 'Blank barcode + IBR has no sort_no/sku',
                      hint: 'Pre-validation BRE row — re-validate the bundle' },
                    { k: 'blank_barcode_sort_no_no_fg',
                      label: 'Blank barcode + sort_no, greige missing fg_name',
                      hint: 'Patch greige_sort_nos.fg_name (matches the trace)' },
                    { k: 'blank_barcode_fg_no_cat2',
                      label: 'Blank barcode + fg resolved, category2 blank',
                      hint: 'Patch item_master.category2 for the fg name' },
                    { k: 'blank_barcode_no_bundle_code',
                      label: 'Blank barcode + no bundle_code',
                      hint: 'Folding entry malformed — investigate the txn' },
                  ].map(({ k, label, hint }) => {
                    const row = drillTotals.uncategorised_breakdown[k] || { usns: 0, bundles: 0 };
                    if (row.usns === 0 && row.bundles === 0) return null;
                    return (
                      <div key={k} className="rounded border border-amber-200 bg-white/70 px-2 py-1.5"
                           data-testid={`uncat-breakdown-${k}`}>
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="font-mono font-semibold text-amber-900">
                            {row.usns.toLocaleString()}
                          </span>
                          <span className="text-[10px] text-amber-700">
                            {row.bundles.toLocaleString()} bundle{row.bundles === 1 ? '' : 's'}
                          </span>
                        </div>
                        <div className="text-[11px] text-stone-800 leading-snug">{label}</div>
                        <div className="text-[10px] text-stone-500 leading-snug mt-0.5">{hint}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-10 text-stone-500">
                <Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading…
              </div>
            ) : groupedByStaff.length === 0 ? (
              <div className="text-center py-10 text-stone-500" data-testid="empty-state">
                No folding transactions in this range.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Staff</TableHead>
                    <TableHead>Category 2</TableHead>
                    <TableHead className="text-right">USNs</TableHead>
                    <TableHead className="text-right">Folds</TableHead>
                    <TableHead className="text-right">Rate / USN</TableHead>
                    <TableHead>Rate Source</TableHead>
                    <TableHead className="text-right">Amount (₹)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groupedByStaff.map((g) => (
                    <React.Fragment key={g.staff_id}>
                      {g.categories.map((r, i) => (
                        <TableRow key={`${g.staff_id}-${r.category2}`}
                                  data-testid={`row-${g.staff_id}-${r.category2}`}>
                          {i === 0 ? (
                            <TableCell className="font-medium align-top" rowSpan={g.categories.length}>
                              <div>{g.staff_id}</div>
                              <div className="text-xs text-stone-500">{g.staff_name}</div>
                            </TableCell>
                          ) : null}
                          <TableCell>
                            {r.category2 === '(uncategorised)' ? (
                              <button type="button"
                                      onClick={openDrillDown}
                                      className="text-amber-800 underline decoration-dotted hover:text-amber-900"
                                      data-testid={`uncat-link-${g.staff_id}`}>
                                {r.category2}
                              </button>
                            ) : r.category2}
                          </TableCell>
                          <TableCell className="text-right">{r.usn_count.toLocaleString()}</TableCell>
                          <TableCell className="text-right">{r.fold_count}</TableCell>
                          <TableCell className="text-right">₹{Number(r.rate_per_usn).toFixed(2)}</TableCell>
                          <TableCell className="text-xs text-stone-600">{r.rate_source}</TableCell>
                          <TableCell className="text-right font-semibold">
                            ₹{Number(r.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-amber-50">
                        <TableCell />
                        <TableCell colSpan={2} className="text-right text-xs font-semibold">
                          Staff subtotal
                        </TableCell>
                        <TableCell />
                        <TableCell />
                        <TableCell className="text-right font-semibold">{g.total_usns.toLocaleString()} USNs</TableCell>
                        <TableCell className="text-right font-semibold text-green-800">
                          ₹{g.total_amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>

      <Dialog open={drillOpen} onOpenChange={setDrillOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col"
                       data-testid="uncat-drill-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-700" />
              Uncategorised Sort Numbers
            </DialogTitle>
            <DialogDescription>
              These sort numbers were folded in the selected period but their <code>item_master</code> row
              is missing a <code>category2</code>. Update Item Master then refresh the report.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-wrap items-center gap-2 text-xs">
            <Badge variant="outline" data-testid="drill-total-skus">
              Distinct sort no.: {drillTotals.uncategorised_sku_count ?? 0}
            </Badge>
            <Badge variant="outline" data-testid="drill-total-usns">
              USNs: {(drillTotals.uncategorised_usns ?? 0).toLocaleString()}
            </Badge>
            <Badge variant="outline" data-testid="drill-in-master">
              In Item Master (missing cat2): {drillTotals.uncategorised_in_master ?? 0}
            </Badge>
            <Badge variant="outline" className="border-rose-300 text-rose-800"
                   data-testid="drill-missing-master">
              Not found in Item Master: {drillTotals.uncategorised_missing_in_master ?? 0}
            </Badge>
          </div>

          <div className="flex items-center gap-2 mt-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-stone-400" />
              <Input value={drillSearch}
                     onChange={(e) => setDrillSearch(e.target.value)}
                     placeholder="Search sort no, design, color, size…"
                     className="pl-8" data-testid="drill-search" />
            </div>
            {isAdmin && (drillTotals.uncategorised_in_master ?? 0) > 0 ? (
              <Button variant="outline" size="sm" onClick={openHealDialog}
                      className="border-emerald-400 bg-emerald-50 text-emerald-900 hover:bg-emerald-100"
                      data-testid="heal-cat2-btn">
                <Wand2 className="w-4 h-4 mr-2" /> Heal cat2 ← cat4
              </Button>
            ) : null}
            <Button variant="outline" size="sm" onClick={handleDrillExportCSV}
                    disabled={drillRows.length === 0}
                    data-testid="drill-export-btn">
              <Download className="w-4 h-4 mr-2" /> Export CSV
            </Button>
          </div>

          <div className="flex-1 overflow-auto border rounded-md mt-2">
            {drillLoading ? (
              <div className="text-center py-10 text-stone-500">
                <Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading…
              </div>
            ) : filteredDrillRows.length === 0 ? (
              <div className="text-center py-10 text-stone-500" data-testid="drill-empty">
                No uncategorised sort numbers in this range.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-amber-50/60 sticky top-0">
                    <TableHead>Sort No / Item Barcode</TableHead>
                    <TableHead>Why uncat?</TableHead>
                    <TableHead>In Master?</TableHead>
                    <TableHead>Design</TableHead>
                    <TableHead>Color / Size</TableHead>
                    <TableHead>Cat 4</TableHead>
                    <TableHead className="text-right">Bundles</TableHead>
                    <TableHead className="text-right">USNs</TableHead>
                    <TableHead>Last Seen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDrillRows.map((r) => (
                    <TableRow key={r.sku_code || '(blank)'}
                              data-testid={`drill-row-${r.sku_code || 'blank'}`}>
                      <TableCell className="font-mono text-xs">
                        {r.sku_code ? r.sku_code : (
                          <div className="flex items-center gap-2">
                            <span className="text-rose-700">(blank)</span>
                            <Button size="sm" variant="outline"
                                    className="h-6 text-xs border-rose-300 text-rose-800"
                                    onClick={openBundleTrace}
                                    data-testid="trace-blank-btn">
                              Trace source
                            </Button>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {r.in_item_master ? (
                          <Badge variant="outline" className="border-amber-300 text-amber-800">
                            Yes (missing cat2)
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="border-rose-300 text-rose-800">
                            Not in Master
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const p = r.legacy_path;
                          const hb = r.had_barcode;
                          const cfg = hb && (!p || p === 'no_ibr' || p === 'no_bundle_code')
                            ? { label: 'Non-blank barcode missing in Item Master', cls: 'border-rose-300 text-rose-800' }
                            : p === 'no_ibr'
                              ? { label: 'No IBR row', cls: 'border-rose-300 text-rose-800' }
                              : p === 'ibr_no_sort_no'
                                ? { label: 'IBR has no sort_no/sku', cls: 'border-amber-300 text-amber-800' }
                                : p === 'sort_no_no_fg'
                                  ? { label: 'greige_sort_nos missing fg', cls: 'border-amber-300 text-amber-800' }
                                  : p === 'fg_no_cat2'
                                    ? { label: 'fg present, category2 blank', cls: 'border-amber-300 text-amber-800' }
                                    : p === 'no_bundle_code'
                                      ? { label: 'No bundle_code', cls: 'border-rose-300 text-rose-800' }
                                      : { label: '—', cls: 'border-stone-300 text-stone-600' };
                          return (
                            <Badge variant="outline" className={`text-[10px] ${cfg.cls}`}
                                   data-testid={`drill-why-${p || 'none'}`}>
                              {cfg.label}
                            </Badge>
                          );
                        })()}
                      </TableCell>

                      <TableCell className="text-xs">
                        <div className="font-semibold">{r.design_name || '—'}</div>
                        <div className="text-stone-500">{r.design_code || ''}</div>
                      </TableCell>
                      <TableCell className="text-xs">
                        <div>{r.color || '—'}</div>
                        <div className="text-stone-500">{r.size || ''}</div>
                      </TableCell>
                      <TableCell className="text-xs">{r.category4 || '—'}</TableCell>
                      <TableCell className="text-right">{r.bundle_count.toLocaleString()}</TableCell>
                      <TableCell className="text-right font-semibold">
                        {r.total_usns.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-xs text-stone-600">
                        {(r.last_seen || '').slice(0, 10) || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setDrillOpen(false)} data-testid="drill-close-btn">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={traceOpen} onOpenChange={setTraceOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col"
                       data-testid="bundle-trace-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-rose-700" />
              Bundle Source Trace — Blank Sort Numbers
            </DialogTitle>
            <DialogDescription>
              For every folding bundle in the selected period that has no sort number, we look it up in
              <code className="px-1 mx-1 bg-stone-100 rounded">inhouse_bundle_receipts</code>,
              <code className="px-1 mx-1 bg-stone-100 rounded">wms_sfg_inventory</code>, and
              <code className="px-1 mx-1 bg-stone-100 rounded">greige_storage_inventory</code>
              to identify the entry path that produced it.
            </DialogDescription>
          </DialogHeader>

          {traceLoading ? (
            <div className="text-center py-16 text-stone-500">
              <Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Tracing bundles…
            </div>
          ) : !traceData ? (
            <div className="text-center py-10 text-stone-500" data-testid="trace-empty">
              No data
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <Badge variant="outline" data-testid="trace-total-bundles">
                  Blank bundles: {traceData.summary.total_blank_bundles_in_folding.toLocaleString()}
                </Badge>
                <Badge variant="outline" data-testid="trace-total-usns">
                  USNs: {traceData.summary.total_blank_usns_in_folding.toLocaleString()}
                </Badge>
                <Badge variant="outline" data-testid="trace-distinct">
                  Distinct codes: {traceData.summary.distinct_blank_bundle_codes.toLocaleString()}
                </Badge>
              </div>

              {/* 2026-05-18 — Legacy-chain resolution banner so the
                  operator sees that the wages report IS resolving these
                  bundles via IBR.sort_no → greige_sort_nos.fg_name →
                  item_master.category2 (not silently dropping them).
                  2026-05-26 — Each row carries a download icon so the
                  master-data team can pull the full bucket as CSV. */}
              {traceData.summary.legacy_chain_summary && (
                <div
                  className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs mt-1"
                  data-testid="legacy-chain-summary"
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-semibold text-emerald-900">
                      Legacy-chain resolution (bundle_code → IBR.sort_no → greige_sort_nos.fg_name → item_master.category2):
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 text-[10px] border-emerald-400 text-emerald-900 hover:bg-emerald-100"
                      onClick={() => handleBucketCSV('all')}
                      disabled={bucketDownloading !== null}
                      data-testid="bucket-csv-all"
                    >
                      {bucketDownloading === 'all'
                        ? <Loader2 className="w-3 h-3 animate-spin mr-1" />
                        : <Download className="w-3 h-3 mr-1" />}
                      Download All
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-1.5 text-emerald-900">
                    {[
                      { key: 'resolved_sort_no_chain', label: '✅ Resolved via sort_no chain', tone: 'emerald' },
                      { key: 'resolved_direct_sku', label: '✅ Resolved via direct sku', tone: 'emerald' },
                      { key: 'unresolved_no_ibr', label: '❌ No IBR row', tone: 'rose' },
                      { key: 'unresolved_no_sort_no', label: '❌ IBR has no sort_no/sku', tone: 'rose' },
                      { key: 'unresolved_no_greige_fg', label: '❌ greige_sort_nos has no fg_name', tone: 'rose', hint: 'Need Sort Nos for this' },
                      { key: 'unresolved_no_item_master_cat2', label: '❌ item_master has no category2', tone: 'rose' },
                    ].map((row) => {
                      const count = traceData.summary.legacy_chain_summary[row.key] || 0;
                      const disabled = bucketDownloading !== null || count === 0;
                      return (
                        <div
                          key={row.key}
                          className={
                            'flex items-center justify-between gap-2 ' +
                            (row.tone === 'rose' ? 'text-rose-800' : '')
                          }
                          data-testid={`bucket-row-${row.key}`}
                        >
                          <span className="flex-1 truncate">
                            {row.label}: <b>{count.toLocaleString()}</b>
                            {row.hint && count > 0 && (
                              <span className="ml-1 text-[10px] italic text-rose-700">— {row.hint}</span>
                            )}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleBucketCSV(row.key)}
                            disabled={disabled}
                            className={
                              'inline-flex items-center justify-center rounded p-1 ' +
                              (disabled
                                ? 'text-stone-400 cursor-not-allowed'
                                : 'text-emerald-700 hover:text-emerald-900 hover:bg-emerald-100')
                            }
                            data-testid={`bucket-csv-${row.key}`}
                            title={count === 0 ? 'Nothing to download' : `Download CSV (${count.toLocaleString()})`}
                          >
                            {bucketDownloading === row.key
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              : <Download className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex-1 overflow-auto border rounded-md mt-2 divide-y">
                {Object.entries(traceData.groups || {}).map(([key, group]) => (
                  <div key={key} className="p-3" data-testid={`trace-group-${key}`}>
                    <button type="button"
                            className="w-full flex items-center justify-between text-left hover:bg-stone-50 -mx-3 px-3 py-1"
                            onClick={() => setTraceExpanded((prev) => ({ ...prev, [key]: !prev[key] }))}>
                      <div className="flex items-center gap-3">
                        <span className="text-stone-400 text-xs">
                          {traceExpanded[key] ? '▼' : '▶'}
                        </span>
                        <div>
                          <div className="font-semibold text-sm">{group.label}</div>
                          <div className="text-xs text-stone-500">
                            {group.count.toLocaleString()} bundle{group.count !== 1 ? 's' : ''} affected
                          </div>
                        </div>
                      </div>
                      <Badge className={key === 'ghost_no_source' ? 'bg-rose-100 text-rose-900' :
                                        key === 'ibr_legacy_admin_migration' ? 'bg-amber-100 text-amber-900' :
                                        'bg-stone-100 text-stone-900'}>
                        {group.count}
                      </Badge>
                    </button>
                    {traceExpanded[key] ? (
                      <div className="mt-2 space-y-2 text-xs">
                        {(group.samples || []).slice(0, 20).map((s, idx) => (
                          <div key={idx} className="border rounded p-2 bg-stone-50/50">
                            <div className="font-mono font-semibold text-stone-900">{s.bundle_code}</div>
                            <div className="text-stone-500">
                              folding: {s.folding_id || '—'} · USNs: {s.usn_count} · {(s.submitted_at || '').slice(0, 10)}
                            </div>
                            {s.ibr ? (
                              <div className="mt-1 text-stone-700">
                                <span className="font-semibold">IBR:</span>{' '}
                                sku=<code>{s.ibr.sku_code || '—'}</code>{' '}
                                sort_no=<code>{s.ibr.sort_no || '—'}</code>{' '}
                                vendor=<code>{s.ibr.vendor_name || '—'}</code>{' '}
                                by=<code>{s.ibr.created_by_name || s.ibr.created_by || '—'}</code>
                                {s.ibr.migrated_from ? <span className="ml-1 px-1 bg-amber-100 rounded">migrated_from:{s.ibr.migrated_from}</span> : null}
                                {s.ibr.heal_source ? <span className="ml-1 px-1 bg-amber-100 rounded">heal:{s.ibr.heal_source}</span> : null}
                                {s.ibr.is_legacy ? <span className="ml-1 px-1 bg-stone-200 rounded">is_legacy</span> : null}
                                {s.ibr.flow_type ? <span className="ml-1 px-1 bg-stone-200 rounded">{s.ibr.flow_type}</span> : null}
                              </div>
                            ) : null}
                            {s.sfg ? (
                              <div className="mt-1 text-stone-700">
                                <span className="font-semibold">SFG:</span>{' '}
                                sku=<code>{s.sfg.sku_code || '—'}</code>{' '}
                                sort_no=<code>{s.sfg.sort_no || '—'}</code>
                              </div>
                            ) : null}
                            {s.greige ? (
                              <div className="mt-1 text-stone-700">
                                <span className="font-semibold">Greige:</span>{' '}
                                fg_name=<code>{s.greige.fg_name || '—'}</code>{' '}
                                sort_no=<code>{s.greige.sort_no || '—'}</code>{' '}
                                vendor=<code>{s.greige.vendor_name || '—'}</code>
                              </div>
                            ) : null}
                            {s.legacy_chain ? (
                              <div
                                className={
                                  "mt-1 rounded px-2 py-1 " +
                                  (s.legacy_chain.resolved_category2
                                    ? "bg-emerald-50 text-emerald-900 border border-emerald-200"
                                    : "bg-rose-50 text-rose-900 border border-rose-200")
                                }
                                data-testid={`legacy-chain-sample-${s.bundle_code}`}
                              >
                                <span className="font-semibold">
                                  {s.legacy_chain.resolved_category2 ? '✅ Chain:' : '❌ Chain:'}
                                </span>{' '}
                                <code>{s.legacy_chain.ibr_sort_no || '—'}</code>{' → fg='}
                                <code>{s.legacy_chain.greige_fg_name || '—'}</code>{' → cat2='}
                                <code>{s.legacy_chain.resolved_category2 || '—'}</code>
                                {s.legacy_chain.im_design_name ? (
                                  <span className="ml-2 text-stone-500">({s.legacy_chain.im_design_name})</span>
                                ) : null}
                                {!s.legacy_chain.resolved_category2 && s.legacy_chain.im_category4 ? (
                                  <span className="ml-2 px-1 bg-amber-100 rounded">cat4-only: {s.legacy_chain.im_category4}</span>
                                ) : null}
                              </div>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ))}
                {Object.keys(traceData.groups || {}).length === 0 ? (
                  <div className="text-center py-8 text-stone-500" data-testid="trace-no-groups">
                    No blank-sort bundles found in this period.
                  </div>
                ) : null}
              </div>
            </>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setTraceOpen(false)} data-testid="trace-close-btn">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={healOpen} onOpenChange={setHealOpen}>
        <DialogContent className="max-w-2xl" data-testid="heal-cat2-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-emerald-700" />
              Backfill Category 2 from Category 4
            </DialogTitle>
            <DialogDescription>
              Sets <code>item_master.category2 = category4</code> for every row where
              category4 is populated but category2 is empty. Idempotent — safe to re-run.
              After heal, the wages report will compute against category2 (no longer flat fallback).
            </DialogDescription>
          </DialogHeader>

          {healLoading && !healPreview ? (
            <div className="text-center py-10 text-stone-500">
              <Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading preview…
            </div>
          ) : healResult ? (
            <div className="rounded-md border border-emerald-300 bg-emerald-50 p-4 text-sm"
                 data-testid="heal-result">
              <div className="font-semibold text-emerald-900">{healResult.message}</div>
              <div className="text-emerald-800 mt-1">
                Candidates: {healResult.candidate_count} · Updated: {healResult.updated_count}
              </div>
            </div>
          ) : healPreview ? (
            <div className="space-y-2 text-sm">
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3"
                   data-testid="heal-preview">
                <div className="font-semibold text-amber-900">
                  {healPreview.candidate_count} item_master rows will be updated.
                </div>
                <div className="text-amber-800 text-xs mt-1">
                  Each row: <code>category2 ← category4</code> + audit fields
                  (<code>category2_healed_at</code>, <code>category2_healed_by</code>).
                </div>
              </div>
              {(healPreview.samples || []).length > 0 ? (
                <div className="text-xs text-stone-600 max-h-48 overflow-auto border rounded-md p-2">
                  <div className="font-semibold mb-1">Sample (first {healPreview.samples.length} of {healPreview.candidate_count}):</div>
                  {healPreview.samples.map((s, i) => (
                    <div key={i} className="font-mono py-0.5">
                      {s.barcode || s.sku_code} → cat4=<code>{s.category4}</code>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setHealOpen(false)} data-testid="heal-close-btn">
              Close
            </Button>
            {healPreview && !healResult ? (
              <Button onClick={runHeal} disabled={healLoading || healPreview.candidate_count === 0}
                      className="bg-emerald-700 hover:bg-emerald-800 text-white"
                      data-testid="heal-confirm-btn">
                {healLoading ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Healing…</>
                ) : (
                  <><Wand2 className="w-4 h-4 mr-2" /> Heal {healPreview.candidate_count} rows</>
                )}
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FoldingCategoryWages;
